import { DateTime } from 'luxon'
import { campaigntrack, realhub } from 'assets/icons'
import config from 'config'

export function digObject(target, keyString, defaultValue) {
  const keys = keyString.split('.')

  const value = keys.reduce(
    (acc, key) => {
      const accValue = typeof acc === 'undefined' || acc === null ? acc : acc[key]
      return accValue
    },
    { ...target }
  )

  if (value === null || value === undefined) return defaultValue

  return value
}

// Normalise an asset into the workbase AssetDto body that POST /assets/push/{platform} accepts (CH-4288).
// Real assets already are that shape, so they pass through. AI edits carry extra client/lineage fields the
// AssetDto has no slot for, so pick only the documented fields and empty the id — an empty id is the BE's
// "this is an AI asset" signal (it stamps the CMS row by cms_asset_id instead of the WB blob).
export function toPushAsset(asset) {
  if (!asset?.isAiEdit) return asset

  const { cms_asset_id, ctservicetype, name, job, project, download_uri, owner, supplier, supplier_reference, uri, metadata, properties } = asset

  return { id: '', cms_asset_id, ctservicetype, name, job, project, download_uri, owner, supplier, supplier_reference, uri, metadata, properties }
}

export function getAvailableProviders(bookableSlots, currentTask) {
  const currentBooking = currentTask?.requisites?.find((x) => x.name === 'Booking')

  return bookableSlots.filter((x) => {
    const start = DateTime.fromISO(x.start, { zone: 'utc' }).toISO({ suppressMilliseconds: true })
    const end = DateTime.fromISO(x.end, { zone: 'utc' }).toISO({ suppressMilliseconds: true })

    const taskStart = DateTime.fromISO(currentTask?.value?.start, { zone: 'utc' }).toISO({ suppressMilliseconds: true })
    const taskEnd = DateTime.fromISO(currentTask?.value?.end, { zone: 'utc' }).toISO({ suppressMilliseconds: true })

    return start <= taskStart && end >= taskEnd && currentBooking?.value?.service_types?.every((service) => x.services?.includes(service))
  })
}

export function getNearestProvider(providerSlots) {
  let availableProviders =
    providerSlots?.sort((a, b) => {
      if (!a?.travel_from) return 1
      if (!b?.travel_from) return -1
      return a.travel_from.duration < b.travel_from.duration ? -1 : 1
    }) || []

  return availableProviders.length ? availableProviders[0] : null
}

export function getPlatform(flags, user = null, office = null, orders = []) {
  const agent = user?.roles?.includes('Agent') || false
  const officeFlags = office?.flags || []
  const orderFlags = orders?.find((order) => order?.annotation?.flags)?.annotation?.flags || null
  const userFlags = user?.flags || []
  const assetDownloadOverride = userFlags.includes('Can_Not_Download_Proofs') || false
  const assetBookingOverride = userFlags.includes('Can_Not_Book_Appointment') || false
  const assetProofOverride = userFlags.includes('Can_Not_Proof') || false
  const vendorShareableLinkOverride = userFlags.includes('Can_Not_Generate_Vendor_Sharing_Links') || false

  let managedFlags = {
    assetDownload: assetDownloadOverride ? false : officeFlags?.includes('AdminOnlyDownload') ? (agent ? false : true) : true,
    bookingAccess: assetBookingOverride ? false : officeFlags?.includes('AdminOnlyBook') ? (agent ? false : true) : true,
    assetProof: assetProofOverride ? false : true,
    vendorShareGenerateLink: vendorShareableLinkOverride ? false : true,
  }

  if (orderFlags?.campaigntrack_reference || flags?.campaigntrack_reference) {
    return {
      ...managedFlags,
      name: 'Campaigntrack',
      campaigntrackPropertyNo: flags?.campaigntrack_reference,
      campaigntrackOrderNo: orderFlags?.campaigntrack_reference,
      // projectFlags
      bookingContacts: true,
      contentGallery: true,
      href: config.ctProjectReferenceUrl,
      logo: campaigntrack,
      marketplace: false,
      pushContentToPlatform: true,
      shoppingCart: false,
      // userFlags
      editProject: false,
      newProject: false,
      profileUsername: false,
    }
  }

  if (flags?.realhub_reference) {
    return {
      ...managedFlags,
      name: 'Realhub',
      // projectFlags
      bookingContacts: false,
      contentGallery: true,
      logo: realhub,
      marketplace: false,
      pushContentToPlatform: true,
      shoppingCart: false,
      // userFlags
      editProject: false,
      newProject: false,
      profileUsername: false,
    }
  }

  return {
    ...managedFlags,
    // projectFlags
    bookingContacts: false,
    contentGallery: true,
    marketplace: true,
    pushContentToPlatform: officeFlags?.includes('Approval') || false,
    shoppingCart: true,
    // userFlags
    editProject: true,
    newProject: true,
    profileUsername: true,
  }
}

// Query options for loading a project's jobs via GET /jobs (CH-4358).
// The natural filter is f=['order']|in|<confirmed order ids>, but APIM rejects URLs
// past its request-line limit (~2KB — a 64-guid filter fails in the wild). Past a
// conservative length budget we filter by project instead (jobs carry `project`
// from creation). That widens the set to jobs of unconfirmed orders too; both
// consumers (Asset view, Project Orders view) join jobs per confirmed order item or
// via asset.project, so the extras are inert in redux.
export const JOBS_FILTER_MAX_CHARS = 1536 // ≈40 GUIDs; encoded URL stays well under 2KB

export function jobsLoadOptions(orders, projectId) {
  const orderFilter = `['order']|in|${(orders || []).toString()}`
  if (projectId && orderFilter.length > JOBS_FILTER_MAX_CHARS) {
    return { size: -1, f: `project|=|${projectId}` }
  }
  return { size: -1, f: orderFilter }
}
